/**
 * The notification type registry + icon spec (Sept 2026 notifications redesign).
 * Handoff: ~/Downloads/design_handoff_notifications (README §"Icon system"), ported from
 * its `notif-types.js` as-is.
 *
 * Every row maps one-to-one to a backend `NotificationType` enum name. The family
 * assignment MUST agree with `Services/NotificationFamilies.cs` — the server filters and
 * counts by family, the client only draws — and the test diffs this list against the
 * families the API answers with.
 *
 * Icons are three layers: the FRAME is the family (a tinted tile; the Portal ring for
 * PORTALS; a black tile with the ring-gradient border for HOST TOOLS), the GLYPH is the
 * subject (24px grid, 2px stroke, square caps, miter joins — hand-built, Lucide-adjacent,
 * not copied) and the BADGE is what happened (a 16px-grid mod coloured by severity).
 */
export const GLYPHS = {
  signal: 'M12 4a5 8 0 1 1 0 16 5 8 0 0 1 0-16z M2 12h3 M19 12h3 M4.5 4.5l2 2 M17.5 17.5l2 2 M19.5 4.5l-2 2 M4.5 19.5l2-2',
  seal: 'M12 2l2.4 2.2 3.2-.4.9 3.1 2.8 1.6-1.2 3 1.2 3-2.8 1.6-.9 3.1-3.2-.4L12 22l-2.4-2.2-3.2.4-.9-3.1-2.8-1.6 1.2-3-1.2-3 2.8-1.6.9-3.1 3.2.4z',
  hand: 'M8 12V5a1.5 1.5 0 0 1 3 0v6 M11 4a1.5 1.5 0 0 1 3 0v7 M14 5a1.5 1.5 0 0 1 3 0v7 M8 11.5a1.5 1.5 0 0 0-3 0V14c0 4 3 7 7 7h1a6 6 0 0 0 6-6V8.5a1.5 1.5 0 0 0-3 0',
  square: 'M3 3h18v18H3z M3 9h18 M9 9v12',
  person: 'M9 3h6v6H9z M4 21v-5l3-3h10l3 3v5',
  people: 'M4 4h5v5H4z M15 4h5v5h-5z M2 20v-4l2-3h5l2 3v4 M13 20v-4l2-3h5l2 3v4',
  link: 'M2 9h7v6H2z M15 9h7v6h-7z M9 12h6',
  circle: 'M3 3h18v18H3z M8 8h8v8H8z M11 11h2v2h-2z',
  bubble: 'M3 4h18v13H9l-6 4z',
  bubbleLines: 'M3 4h18v13H9l-6 4z M7 9h10 M7 13h6',
  bubblePlus: 'M3 4h18v13H9l-6 4z M12 7v6 M9 10h6',
  spark: 'M12 2l2.5 7.5L22 12l-7.5 2.5L12 22l-2.5-7.5L2 12l7.5-2.5z',
  form: 'M5 3h14v18H5z M8.5 8h7 M8.5 12h7 M8.5 16h4',
  flag: 'M5 22V3 M5 4h13l-3 4.5 3 4.5H5',
  calendar: 'M3 5h18v16H3z M3 10h18 M8 2v5 M16 2v5',
  hourglass: 'M5 3h14 M5 21h14 M7 3v4l5 5 5-5V3 M7 21v-4l5-5 5 5v4',
  envelope: 'M3 5h18v14H3z M3 6l9 7 9-7',
  megaphone: 'M3 9h4l9-5v16l-9-5H3z M7 15l1.5 6h3L10 16 M19.5 9.5v5',
  shield: 'M12 2l8 3v7c0 5-3.5 8-8 10-4.5-2-8-5-8-10V5z',
  note: 'M4 4h9 M4 4v16h16v-9 M18 2l4 4-9 9H9v-4z',
  queue: 'M3 5h18 M3 10h18 M3 15h18 M3 20h9',
  sync: 'M4 11a8 8 0 0 1 14.5-4.5 M19 2v5h-5 M20 13a8 8 0 0 1-14.5 4.5 M5 22v-5h5',
  plug: 'M9 2v5 M15 2v5 M5 7h14v4a7 7 0 0 1-14 0z M12 18v4',
  ticket: 'M2 6h20v4a2 2 0 0 0 0 4v4H2v-4a2 2 0 0 0 0-4z M15 6v2 M15 11v2 M15 16v2',
  swap: 'M3 8h16 M15 4l4 4-4 4 M21 16H5 M9 12l-4 4 4 4',
  bag: 'M4 8h16l-1.5 13h-13z M9 11V4h6v7',
  dollar: 'M12 2v20 M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
  wallet: 'M3 7h18v14H3z M3 7l14-4v4 M15 12h6v4h-6z',
  undo: 'M9 14L4 9l5-5 M4 9h11a5 5 0 0 1 0 10h-4',
  unlock: 'M4 11h16v11H4z M8 11V7a4 4 0 0 1 7.7-1.5 M12 15v3',
  bank: 'M2 9l10-6 10 6z M5 9v9 M9.5 9v9 M14.5 9v9 M19 9v9 M2 21h20',
  idcard: 'M2 5h20v14H2z M6 9h4v4H6z M5 16h6 M14 10h5 M14 14h5',
  mask: 'M4 3h16v9a8 8 0 0 1-16 0z M8 8h2 M14 8h2 M9 15h6',
  wand: 'M3 21l11-11 M12 8l4 4 M18 2v4 M16 4h4 M20 10v2 M19 11h2',
  box: 'M3 7l9-4 9 4v10l-9 4-9-4z M3 7l9 4 9-4 M12 11v10',
  house: 'M3 10l9-7 9 7v11H3z M9 21v-7h6v7',
  door: 'M5 3h14v18H5z M2 21h3 M19 21h3 M15 12v1',
  poll: 'M5 21V11 M12 21V4 M19 21v-7 M2 21h20',
  card: 'M2 5h20v14H2z M2 10h20 M6 15h5',
  gift: 'M3 8h18v5H3z M5 13h14v8H5z M12 8v13 M12 8C9 3 5 4 6.5 7 M12 8c3-5 7-4 5.5-1',
  chart: 'M3 3v18h18 M7 15l4-4 3 3 6-7',
  portal: 'M12 3a9 9 0 1 1 0 18 9 9 0 0 1 0-18z M12 8a4 4 0 1 1 0 8 4 4 0 0 1 0-8z',
  key: 'M3 12a4 4 0 1 0 8 0 4 4 0 0 0-8 0z M11 12h10 M17 12v4 M21 12v3',
  stanchion: 'M5 6v14 M19 6v14 M2 21h6 M16 21h6 M5 9c3 5 11 5 14 0 M4 3h2v2H4z M18 3h2v2h-2z',
  tag: 'M3 3h9l9 9-9 9-9-9z M7 7h2v2H7z',
  counter: 'M7 20V4 M3 8l4-4 4 4 M17 4v16 M13 16l4 4 4-4',
  bolt: 'M13 2L4 14h7l-1 8 9-12h-7z',
  eye: 'M2 12c3-5 6.5-7 10-7s7 2 10 7c-3 5-6.5 7-10 7S5 17 2 12z M10 10h4v4h-4z',
  store: 'M3 3h18l1 6H2z M4 9v12h16V9 M9 21v-6h6v6',
  sprout: 'M12 22V12 M12 12C12 7 9 5 3 5c0 5 3 7 9 7z M12 12c0-5 3-8 9-8 0 6-3 8-9 8z',
  list: 'M9 5h12 M9 12h12 M9 19h12 M3 4h2v2H3z M3 11h2v2H3z M3 18h2v2H3z',
  plane: 'M22 2L2 10l8 4 4 8z M10 14L22 2',
  badge: 'M9 2h6 M12 2v3 M4 5h16v17H4z M9 10h6v5H9z M8 18h8',
  bracket: 'M8 3H4v18h4 M16 3h4v18h-4 M9 12h2 M13 12h2',
};

export const MODS = {
  check: 'M3 8.5l3 3 7-7', x: 'M4 4l8 8 M12 4l-8 8', plus: 'M8 3v10 M3 8h10', minus: 'M3 8h10',
  clock: 'M8 2a6 6 0 1 1 0 12 6 6 0 0 1 0-12z M8 5v3.5h2.5', bang: 'M8 2v7 M8 12v2', arrow: 'M4 12L12 4 M6 4h6v6',
  slash: 'M3 13L13 3', dollar: 'M8 1v14 M11 4H6.5a2 2 0 0 0 0 4h3a2 2 0 0 1 0 4H5',
  refresh: 'M3 8a5 5 0 0 1 9-3 M12 2v3H9 M13 8a5 5 0 0 1-9 3 M4 14v-3h3', up: 'M8 13V3 M4 7l4-4 4 4', down: 'M8 3v10 M4 9l4 4 4-4',
  at: 'M10 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0z M10 6v3a1.5 1.5 0 0 0 3 0V8a5 5 0 1 0-2 4', reply: 'M7 4L3 8l4 4 M3 8h7a3 3 0 0 1 3 3v2',
  ring: 'M8 3a5 5 0 1 1 0 10 5 5 0 0 1 0-10z',
};

/** Badge colour by severity — pos / warn ("needs you") / neg / neu. */
export const SEV = {
  pos: { bg: '#22C55E', fg: '#000', label: 'POSITIVE' },
  warn: { bg: '#FF00AA', fg: '#fff', label: 'NEEDS YOU' },
  neg: { bg: '#EF4444', fg: '#fff', label: 'PROBLEM' },
  neu: { bg: '#000000', fg: '#fff', label: 'NEUTRAL' },
};

/** The initial-tile tints for a face with no photo. */
export const FACE_TINTS = ['#A8D8EA', '#FFCCE6', '#C6B8FF', '#FAE1B4', '#00FF7F'];

/**
 * The nine families, in aside order. `key` is the API's family key: "signals" is PORTALS
 * (the code keeps the old word), "portal" is Portal Noir MEMBERSHIP.
 */
export const FAMILIES = [
  { key: 'signals', label: 'PORTALS', bg: '#000000', fg: '#00FFFF', glyph: 'signal', frame: 'ring' },
  { key: 'people', label: 'PEOPLE', bg: '#FFCCE6', fg: '#000', glyph: 'person' },
  { key: 'events', label: 'EVENTS', bg: '#C6B8FF', fg: '#000', glyph: 'calendar' },
  { key: 'money', label: 'TICKETS + MONEY', bg: '#00FF7F', fg: '#000', glyph: 'dollar' },
  { key: 'clubs', label: 'CLUBS', bg: '#A8D8EA', fg: '#000', glyph: 'house' },
  { key: 'pros', label: 'PROS + BOOKINGS', bg: '#FAE1B4', fg: '#000', glyph: 'tag' },
  { key: 'host', label: 'HOST TOOLS', bg: '#000000', fg: '#FFFFFF', glyph: 'stanchion', frame: 'host' },
  { key: 'portal', label: 'MEMBERSHIP', bg: '#F2F2F2', fg: '#000', glyph: 'seal' },
  { key: 'system', label: 'SYSTEM', bg: '#E5E7EB', fg: '#000', glyph: 'bracket' },
];

// enum | label | family | glyph | mod | severity
const RAW = `
SignalReceived|NEW PORTAL|signals|signal||neu
PortalSignalReceived|NEW PORTAL|signals|signal|ring|neu
SignalMention|TAGGED YOU|signals|signal|at|neu
PortalSignalInvited|PORTAL INVITE|signals|signal|plus|neu
PortalSignalInviteDeclined|INVITE DECLINED|signals|signal|x|neg
PortalSignalReplied|PORTAL REPLY|signals|signal|reply|neu
PortalSignalApplicationReceived|WANTS IN|signals|hand|plus|warn
PortalSignalAccepted|SOMEONE'S IN|signals|hand|check|pos
SignalNotThisOne|NOT THIS ONE|signals|signal|slash|neu
ForumMention|TAGGED YOU|signals|square|at|neu
ForumReply|REPLIED|signals|square|reply|neu
FriendRequestReceived|FRIEND REQUEST|people|person|plus|warn
FriendRequestAccepted|FRIEND|people|person|check|pos
IntroductionReceived|INTRO REQUEST|people|people|plus|warn
IntroductionCompleted|NEW CONNECTION|people|link|check|pos
IntroductionDeclined|INTRO PASSED|people|link|slash|neu
CloseFriendAdded|INNER CIRCLE|people|circle|plus|pos
MessageReceived|MESSAGE|people|bubbleLines||neu
MessageMention|TAGGED YOU|people|bubble|at|neu
MessageReaction|REACTION|people|bubblePlus||neu
MimeRequestReceived|MIME|people|mask|plus|warn
MimeNudge|MIME|people|mask|bang|warn
MimeRequestAccepted|MIME ACCEPTED|people|mask|check|pos
MimeRequestDeclined|MIME DECLINED|people|mask|x|neg
PartyTrickProposed|PARTY TRICK|people|wand|plus|neu
PartyTrickReviewed|PARTY TRICK|people|wand|check|pos
GiveTakePostClaimed|CLAIMED|people|box|check|pos
ApplicationReceived|NEW APPLICATION|events|form|plus|warn
ApplicationWithdrawn|WITHDRAWN|events|form|minus|neu
ApplicationAccepted|ACCEPTED|events|form|check|pos
ApplicationDeclined|DECLINED|events|form|x|neg
EventGreenlit|GREENLIT|events|flag|check|pos
EventCanceled|CANCELED|events|calendar|x|neg
EventEnded|ENDED|events|hourglass||neu
EventInviteReceived|EVENT INVITE|events|envelope||neu
EventAnnouncementPosted|EVENT POST|events|megaphone||neu
EventFeatured|FEATURED|events|spark||pos
FeedbackReceived|FEEDBACK|events|note||neu
WaitlistSpotAvailable|SPOT OPEN|events|queue|clock|warn
WaitlistJoined|WAITLISTED|events|queue|plus|neu
ExternalEventUpdated|EVENT UPDATED|events|sync|bang|warn
EventUpdatedClaimApproved|CLAIM APPROVED|events|sync|check|pos
EventUpdatedClaimRejected|CLAIM DECLINED|events|sync|x|neg
TicketPurchased|TICKET|money|ticket|up|pos
TicketTransferClaimed|CLAIMED|money|ticket|check|pos
TicketTransferred|TRANSFER|money|ticket|arrow|neu
GuestCheckoutReceived|TICKET|money|bag|up|pos
PaymentReceived|PAYMENT|money|dollar|down|pos
PaymentReminderSent|PAYMENT DUE|money|dollar|clock|warn
WalletTopUpCompleted|TOP UP|money|wallet|plus|pos
CommitmentRefunded|REFUND|money|undo||neu
EscrowReleased|ESCROW|money|unlock|check|pos
WithdrawalCompleted|PAYOUT SENT|money|bank|check|pos
WithdrawalFailed|PAYOUT FAILED|money|bank|x|neg
PeerTransferReceived|TRANSFER|money|swap|dollar|pos
PartyPatronGiftReceived|PATRON GIFT|money|gift||pos
EventSponsorGiftReceived|SPONSOR GIFT|money|gift|dollar|pos
BrandConversionReport|BRAND REPORT|money|chart||neu
ClubInvite|CLUB INVITE|clubs|house|arrow|neu
GatheringInviteReceived|GATHERING INVITE|clubs|calendar|plus|neu
GatheringCoHostAdded|CO-HOST|clubs|calendar|plus|pos
GatheringEntryApplied|ENTRY REQUEST|clubs|door|plus|warn
GatheringEntryAccepted|YOU'RE IN|clubs|door|check|pos
GatheringEntryDeclined|NOT THIS TIME|clubs|door|x|neg
GatheringEntryPaid|ENTRY PAID|money|ticket|check|pos
GatheringEntryReceived|ENTRY SOLD|money|ticket|dollar|pos
GatheringEntryRefunded|ENTRY REFUNDED|money|undo||neu
GatheringGreenlit|GREENLIT|clubs|calendar|check|pos
GatheringDissolved|DISSOLVED|clubs|calendar|x|neg
GatheringCanceled|CANCELED|clubs|calendar|x|neg
GatheringEscrowReleased|ESCROW|money|unlock|check|pos
ClubAnnouncementNew|ANNOUNCEMENT|clubs|megaphone||neu
ClubAnnouncementReply|REPLY|clubs|megaphone|reply|neu
ClubMemberJoined|NEW MEMBER|clubs|person|plus|pos
ClubMemberRemoved|REMOVED|clubs|person|minus|neg
ClubPollCreated|POLL|clubs|poll||neu
ClubJoinRequestReceived|JOIN REQUEST|clubs|door|plus|warn
ClubJoinRequestAccepted|WELCOME|clubs|door|check|pos
ClubJoinRequestDeclined|REQUEST DECLINED|clubs|door|x|neg
ClubJoinRequestBlocked|REQUEST BLOCKED|clubs|door|slash|neg
ClubMembershipPurchased|MEMBERSHIP|clubs|card|check|pos
ClubMembershipRenewed|RENEWED|clubs|card|refresh|pos
ClubMembershipRenewalFailed|RENEWAL FAILED|clubs|card|x|neg
ClubMembershipExpiring|EXPIRING|clubs|card|clock|warn
ClubMembershipExpired|EXPIRED|clubs|card|minus|neu
ClubMembershipRefunded|REFUNDED|clubs|card|dollar|neu
ClubMembershipCanceled|CANCELED|clubs|card|slash|neg
ProOfferReceived|OFFER|pros|tag|plus|warn
ProOfferSent|OFFER SENT|pros|tag|arrow|neu
ProOfferCountered|COUNTER OFFER|pros|counter||warn
ProOfferAccepted|OFFER ACCEPTED|pros|tag|check|pos
ProOfferDeclined|OFFER DECLINED|pros|tag|x|neg
ProBookingConfirmed|BOOKED|pros|calendar|check|pos
ProEscrowReleased|PAYOUT RELEASED|pros|unlock|dollar|pos
ProBookingCanceled|BOOKING CANCELLED|pros|calendar|x|neg
ProRescheduleRequested|NEW TIME ASKED|pros|calendar|clock|warn
ProRescheduled|RESCHEDULED|pros|calendar|check|pos
ProRescheduleDeclined|NEW TIME DECLINED|pros|calendar|x|neg
ProBookingFlagged|FLAGGED|pros|flag|bang|neg
ProPageNotice|FROM THE PAGE|pros|megaphone||neu
ProFlagResolved|FLAG RESOLVED|pros|flag|check|pos
ProPaymentFailed|PAYMENT FAILED|pros|dollar|x|neg
ListedProRequestSent|REQUEST SENT|pros|store|arrow|neu
ListedProRequestConverted|NOW AN OFFER|pros|tag|refresh|pos
ListedProClaimConfirmed|PAGE CLAIMED|pros|store|check|pos
ListedProClaimNeedsReview|CLAIM TO REVIEW|pros|store|bang|warn
ListedProRemovalRequested|REMOVAL REQUEST|pros|store|minus|warn
ListedProRunFinished|SEEDING DONE|pros|sprout|check|pos
ProPageTransferRequested|TRANSFER REQUEST|pros|key||warn
ProPageTransferred|NEW OWNER|pros|key|check|pos
ProPageTransferDeclined|TRANSFER DECLINED|pros|key|x|neg
ProPageTransferExpired|TRANSFER EXPIRED|pros|key|x|neg
ProPageOfferPosted|NEW OFFER|pros|tag|plus|pos
PartyEngineClaimInvite|HOST WANTED|host|stanchion|plus|warn
PartyEngineClaimConfirmed|YOU'RE THE HOST|host|stanchion|check|pos
PartyEngineReviewReady|REVIEW READY|host|eye||warn
PartyEngineEventClaimed|CLAIMED|host|stanchion|arrow|neu
PartyEngineEventDissolvedUnclaimed|DISSOLVED|host|stanchion|slash|neg
PartyEngineRunFailed|RUN FAILED|host|bolt|x|neg
PlannerPlanReady|PLAN READY|host|list|check|pos
PlannerPlanFailed|PLAN HELD|host|list|clock|warn
PlannerPaymentFailed|PAYMENT FAILED|host|wallet|x|neg
PlannerOffersSent|OFFERS SENT|host|plane||neu
PortalApplicationAccepted|ACCEPTED|portal|seal|check|pos
PortalApplicationDeclined|NOT THIS TIME|portal|seal|x|neg
PortalApplicationNotYet|NOT YET|portal|seal|clock|warn
PortalInviteUsed|INVITE USED|portal|envelope|check|pos
PortalAdminPromoted|ADMIN|portal|key|up|pos
PortalAdminDemoted|ADMIN|portal|key|down|neu
PortalMembershipRemoved|REMOVED|portal|seal|minus|neg
VerificationSubmitted|VERIFICATION|system|shield|clock|warn
IdentityVerified|VERIFIED|system|idcard|check|pos
IngestionSourceFailing|SOURCE FAILING|system|plug|bang|neg
StaffAccessGranted|STAFF ACCESS|system|badge|plus|pos
StaffAccessChanged|ACCESS CHANGED|system|badge|refresh|neu
StaffAccessRevoked|ACCESS REMOVED|system|badge|minus|neg
AdminAnnouncement|ANNOUNCEMENT|system|bracket||neu`;

export const TYPES = {};
export const TYPE_LIST = RAW.trim().split('\n').map((l) => {
  const [id, label, family, glyph, mod, sev] = l.split('|');
  const t = { id, label, family, glyph, mod: mod || null, sev };
  TYPES[id] = t;
  return t;
});

export function familyOf(key) {
  return FAMILIES.find((f) => f.key === key) || null;
}

/**
 * The registry row for a type — or, for an unknown type (a backend enum value added
 * after this list), a neutral row in the family the API named so the row still draws.
 */
export function typeOf(typeId, family = null) {
  if (TYPES[typeId]) return TYPES[typeId];
  // A family key (the aside draws the family alone) resolves to the family itself.
  const f = familyOf(typeId) || familyOf(family) || FAMILIES[FAMILIES.length - 1];
  return { id: typeId, label: '', family: f.key, glyph: f.glyph, mod: null, sev: 'neu' };
}

/** A stable tint for a person with no photo, from their id or name. */
export function faceTint(seed = '') {
  let h = 0;
  const s = String(seed);
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return FACE_TINTS[Math.abs(h) % FACE_TINTS.length];
}
